<template>
  <div
    class="app-alert"
    :class="alertClasses"
    :role="ariaRole">
    <i
      v-if="showIcon"
      class="app-alert__icon"
      :class="iconClass"
      aria-hidden="true" />

    <div class="app-alert__content">
      <p v-if="title" class="app-alert__title">
        {{ title }}
      </p>
      <div v-if="$slots.default" class="app-alert__message">
        <slot />
      </div>

      <div v-if="$slots.actions" class="app-alert__actions">
        <slot name="actions" />
      </div>
    </div>

    <button
      v-if="dismissible"
      type="button"
      class="app-alert__dismiss"
      :aria-label="dismissLabel"
      @click="emit('dismiss')">
      <i class="fa-solid fa-xmark" aria-hidden="true" />
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'info',
    validator: (value) => ['success', 'danger', 'warning', 'info', 'neutral'].includes(value)
  },
  appearance: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'solid', 'accent'].includes(value)
  },
  size: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'sm', 'lg', 'compact'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    default: ''
  },
  hideIcon: {
    type: Boolean,
    default: false
  },
  dismissible: {
    type: Boolean,
    default: false
  },
  dismissLabel: {
    type: String,
    default: 'Dismiss message'
  }
})

const emit = defineEmits(['dismiss'])

const variantIcons = {
  success: 'fa-solid fa-circle-check',
  danger: 'fa-solid fa-circle-xmark',
  warning: 'fa-solid fa-triangle-exclamation',
  info: 'fa-solid fa-circle-info',
  neutral: 'fa-solid fa-circle-info'
}

const smallVariantIcons = {
  success: 'fa-solid fa-check',
  danger: 'fa-solid fa-xmark',
  warning: 'fa-solid fa-exclamation',
  info: 'fa-solid fa-info',
  neutral: 'fa-solid fa-info'
}

const showIcon = computed(() => !props.hideIcon)

const iconClass = computed(() => {
  if (props.icon) return props.icon
  if (props.size === 'sm') return smallVariantIcons[props.variant]
  return variantIcons[props.variant]
})

const alertClasses = computed(() => {
  const classes = []

  const appearancePrefix = props.appearance !== 'default' ? `${props.appearance}-` : ''
  classes.push(`app-alert--${appearancePrefix}${props.variant}`)

  if (props.size !== 'default') {
    classes.push(`app-alert--${props.size}`)
  }

  return classes
})

const ariaRole = computed(() => (['danger', 'warning'].includes(props.variant) ? 'alert' : 'status'))
</script>
